import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BanknotesIcon, CreditCardIcon, ExclamationTriangleIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { headerCls, DataTableSkeleton } from '../../../data/columns';
import companyApi from '../../../services/companyApi';
import financeApi from '../../../services/financeApi';
import Notification from '../../../components/notification/notification';
import { formatCurrency } from '../../../utils/baseUtils';
import { getSubString } from '../../../utils/getSubString';
import PaymentModal from '../components/PaymentModal';
import { TWButton, TWDataTable, EmptyState, UserAvatar } from '../../../components/ui';
import { BALANCE_THRESHOLD } from './overview-summary';

const cn = (...classes) => classes.filter(Boolean).join(' ');

export const BalancePill = ({ balance }) => {
  const positive = balance > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-sm font-bold ring-1 tabular-nums',
        positive
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20'
          : 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20',
      )}
    >
      {formatCurrency(balance)}
    </span>
  );
};

const getLast4AccountNumber = (company) => {
  const defaultBankAccount = company.bankaccounts.find(
    (x) => x.isdefault && !x.isdeleted,
  );
  return defaultBankAccount
    ? `····  ${getSubString(defaultBankAccount?.accountnumber, 4)}`
    : '-';
};

// The Payouts tab (spec 2026-09-24): what Mercury still owes legacy
// companies. Lifted unchanged from finance.js's former admin branch —
// Connect companies are paid by Stripe and never show up in this table.
const PayoutsTab = () => {
  const [companyList, setCompanyList] = useState([]);
  const [dialogs, setDialogs] = useState({ pay: false });
  const [loading, setLoading] = useState({
    listLoading: true,
    payLoading: false,
  });
  const [listError, setListError] = useState(false);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });
  const [selectedCompany, setSelectedCompany] = useState([]);

  const getCompanyList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setListError(false);
    try {
      const query = `?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}&hasbalance=true`;
      const list = await companyApi.list(query);
      if (list.data.data.length > 0) {
        setCompanyList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      } else {
        setCompanyList([]);
      }
    } catch (error) {
      setListError(true);
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onClickPay = () => setDialogs((prev) => ({ ...prev, pay: true }));
  const onClosePay = () => setDialogs((prev) => ({ ...prev, pay: false }));

  const onSubmitPay = async () => {
    setLoading((prev) => ({ ...prev, payLoading: true }));
    try {
      await Promise.all(
        selectedCompany.map((company) => financeApi.pay(company.id)),
      );
      onClosePay();
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setSelectedCompany([]);
      getCompanyList();
      setLoading((prev) => ({ ...prev, payLoading: false }));
    }
  };

  const eligibleCompanies = companyList.filter(
    (company) =>
      company.bankaccounts?.some(
        (account) =>
          account.isdefault &&
          account.routingnumber &&
          account.address &&
          account.city,
      ) &&
      !company.companysettings?.some(
        (s) => s.code === 'payout_paused' && s.value === 'true',
      ),
  );
  const filteredCompanyList = companyList.filter(
    (company) => Math.abs(company.balance) >= BALANCE_THRESHOLD,
  );

  useEffect(() => {
    getCompanyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber]);

  return (
    <section className="space-y-4" aria-labelledby="mercury-payouts-heading">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="mercury-payouts-heading" className="text-base font-bold text-slate-900 dark:text-slate-100">
            Legacy payouts
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            What Mercury still owes companies on the legacy model. Companies on Connect are paid by Stripe and never appear here.
          </p>
        </div>
        <TWButton
          variant="primary"
          icon={BanknotesIcon}
          disabled={selectedCompany.length === 0}
          onClick={onClickPay}
        >
          Pay{selectedCompany.length > 0 ? ` (${selectedCompany.length})` : ''}
        </TWButton>
      </div>

      {loading.listLoading ? (
        <DataTableSkeleton
          rows={6}
          headers={[
            { label: 'Company', w: 'w-40', shape: 'avatar' },
            { label: 'Bank Account', w: 'w-24' },
            { label: 'Balance', align: 'right', w: 'w-24', shape: 'pill' },
          ]}
        />
      ) : listError ? (
        <EmptyState
          icon={ExclamationTriangleIcon}
          title="Couldn't load the balances."
          action={<TWButton size="sm" label="Try again" onClick={getCompanyList} />}
        />
      ) : filteredCompanyList.length === 0 ? (
        <EmptyState
          icon={ScaleIcon}
          title="No pending balances"
          description="All companies are settled. New payables will appear here."
        />
      ) : (
        <div className="reveal">
          <TWDataTable
            columns={[
              {
                title: <span className={headerCls}>Company</span>,
                key: 'company',
                render: (_, company) => (
                  <div className="flex items-center gap-3 min-w-0">
                    {company.logo ? (
                      <img
                        alt="logo"
                        className="h-10 w-10 shrink-0 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                        src={company.logo}
                      />
                    ) : (
                      <UserAvatar
                        name={company.name}
                        size="md"
                        className="!rounded-md"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/dashboard/companies/${company.id}`}
                        className="truncate text-sm font-semibold text-slate-900 hover:text-blue-600 dark:text-slate-100 dark:hover:text-sky-400"
                      >
                        {company.name}
                      </Link>
                      {company.subscription?.name && (
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                          {company.subscription.name}
                        </p>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                title: <span className={headerCls}>Bank Account</span>,
                key: 'bank',
                render: (_, company) => (
                  <span className="inline-flex items-center gap-1.5 tabular-nums text-sm text-slate-600 dark:text-slate-300">
                    <CreditCardIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    {getLast4AccountNumber(company)}
                  </span>
                ),
              },
              {
                title: (
                  <span className={`${headerCls} block text-right`}>
                    Balance
                  </span>
                ),
                key: 'balance',
                align: 'right',
                render: (_, company) => (
                  <BalancePill balance={company?.balance} />
                ),
              },
            ]}
            data={filteredCompanyList}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: selectedCompany.map((c) => c.id),
              onChange: (_keys, rows) => setSelectedCompany(rows),
              getCheckboxProps: (record) => ({
                disabled: !eligibleCompanies.includes(record),
              }),
              preserveSelectedRowKeys: true,
            }}
            pagination={
              pagination.totalRecords > pagination.pageSize
                ? {
                    current: pagination.pageNumber,
                    pageSize: pagination.pageSize,
                    total: pagination.totalRecords,
                    showTotal: (total, range) =>
                      `Showing ${range[0]} to ${range[1]} of ${total} companies with pending balance`,
                    onChange: (page) =>
                      setPagination((prev) => ({
                        ...prev,
                        pageNumber: page,
                      })),
                    showSizeChanger: false,
                  }
                : undefined
            }
          />
        </div>
      )}

      <PaymentModal
        show={dialogs.pay}
        onClose={onClosePay}
        companies={selectedCompany}
        onSubmit={onSubmitPay}
        loading={loading.payLoading}
      />

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </section>
  );
};

export default PayoutsTab;
